"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  useToast,
  FormErrorMessage,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { TbCalendarUp } from "react-icons/tb";
import { TimezoneSelect } from "./TimezoneSelect";

interface BookAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: any) => Promise<void>;
  initialValues?: any;
}

export const BookAppointmentModal: React.FC<BookAppointmentModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialValues,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [calApiKey, setCalApiKey] = useState("");
  const [eventTypeId, setEventTypeId] = useState("");
  const [timezone, setTimezone] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [nameError, setNameError] = useState("");

  const toast = useToast();

  useEffect(() => {
    if (initialValues) {
      setName(initialValues.name || "");
      setDescription(initialValues.description || "");
      setCalApiKey(initialValues.cal_api_key || "");
      setEventTypeId(initialValues.event_type_id?.toString() || "");
      setTimezone(initialValues.timezone || "");
    } else {
      setName("book_appointment");
      setDescription(
        "When users ask to book an appointment, book it on the calendar."
      );
      setCalApiKey("");
      setEventTypeId("");
      setTimezone("America/Los_Angeles");
    }
  }, [initialValues]);

  const validateToolName = (val: string) => {
    if (!val.trim()) {
      setNameError("Tool name cannot be empty");
      return false;
    }
    if (val.length > 64) {
      setNameError("Tool name must be 64 characters or less");
      return false;
    }
    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validNameRegex.test(val)) {
      setNameError(
        "Tool name must only contain letters, numbers, underscores, and dashes (no spaces)"
      );
      return false;
    }
    setNameError("");
    return true;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setNameError("");
  };

  const handleSave = async () => {
    if (!validateToolName(name)) {
      toast({
        title: "Invalid tool name",
        description: nameError,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    try {
      await onSave({
        type: "book_appointment_cal",
        name,
        description,
        cal_api_key: calApiKey,
        event_type_id: Number.parseInt(eventTypeId) || 0,
        timezone,
      });
      toast({
        title: "Saved",
        status: "success",
        duration: 1500,
      });
      onClose();
    } catch (error: any) {
      console.error("Error saving function:", error);
      toast({
        title: "Error saving function",
        description:
          error?.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const isEventTypeIdValid = eventTypeId === "" || /^\d+$/.test(eventTypeId);

  // Shared orange focus styles (prevents theme primary bleed)
  const orangeFocus = {
    borderColor: "orange.500",
    boxShadow: "0 0 0 1px var(--chakra-colors-orange-500)",
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent
        bg="white"
        color="gray.800"
        borderRadius="xl"
        border="1px"
        borderColor="orange.200"
        _focusWithin={{
          borderColor: "orange.300",
          boxShadow: "0 0 0 1px var(--chakra-colors-orange-300)",
        }}
      >
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={TbCalendarUp} boxSize={5} color="orange.600" />
            <span>Book on the Calendar (Cal.com)</span>
          </HStack>
        </ModalHeader>

        <ModalCloseButton
          _hover={{ bg: "orange.50", color: "orange.700" }}
          _active={{ bg: "orange.100" }}
          _focusVisible={{
            boxShadow: "0 0 0 1px var(--chakra-colors-orange-500)",
          }}
        />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired isInvalid={!!nameError}>
              <FormLabel>Name</FormLabel>
              <Input
                id="book-appointment-function-name"
                value={name}
                onChange={handleNameChange}
                onBlur={() => validateToolName(name)}
                placeholder="Enter function name"
                variant="outline"
                borderColor="gray.300"
                focusBorderColor="orange.400"
                _placeholder={{ color: "gray.500" }}
                _hover={{ borderColor: "orange.300" }}
                _focus={orangeFocus}
                bg="white"
              />
              {nameError && <FormErrorMessage>{nameError}</FormErrorMessage>}
            </FormControl>

            <FormControl>
              <FormLabel>Description (Optional)</FormLabel>
              <Textarea
                id="book-appointment-function-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter function description"
                variant="outline"
                borderColor="gray.300"
                focusBorderColor="orange.400"
                _placeholder={{ color: "gray.500" }}
                _hover={{ borderColor: "orange.300" }}
                _focus={orangeFocus}
                bg="white"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>API Key (Cal.com)</FormLabel>
              <Input
                id="book-appointment-function-api-key"
                value={calApiKey}
                type="password"
                onChange={(e) => setCalApiKey(e.target.value)}
                placeholder="Enter Cal.com API key"
                variant="outline"
                borderColor="gray.300"
                focusBorderColor="orange.400"
                _placeholder={{ color: "gray.500" }}
                _hover={{ borderColor: "orange.300" }}
                _focus={orangeFocus}
                bg="white"
              />
            </FormControl>

            <FormControl isRequired isInvalid={!isEventTypeIdValid}>
              <FormLabel>Event Type ID (Cal.com)</FormLabel>
              <Input
                id="book-appointment-function-event-type-id"
                value={eventTypeId}
                onChange={(e) => setEventTypeId(e.target.value)}
                placeholder="Enter Event Type ID"
                variant="outline"
                borderColor="gray.300"
                focusBorderColor="orange.400"
                _placeholder={{ color: "gray.500" }}
                _hover={{ borderColor: "orange.300" }}
                _focus={orangeFocus}
                bg="white"
              />
              {!isEventTypeIdValid && (
                <FormErrorMessage>
                  Event Type ID must be numeric
                </FormErrorMessage>
              )}
            </FormControl>

            <TimezoneSelect
              value={timezone}
              onChange={setTimezone}
              label="Timezone (Optional)"
              placeholder="Search or select timezone (e.g., America/Los_Angeles)"
            />
          </VStack>
        </ModalBody>

        <ModalFooter gap={3}>
          <Button
            variant="ghost"
            onClick={onClose}
            isDisabled={isSaving}
            color="black"
            _hover={{ bg: "gray.100" }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSave}
            isLoading={isSaving}
            loadingText="Saving..."
            isDisabled={!isEventTypeIdValid || !calApiKey || !!nameError}
            colorScheme="orange"
            bg="orange.500"
            color="white"
            _hover={{ bg: "orange.600" }}
            _active={{ bg: "orange.700" }}
            _focusVisible={{
              boxShadow: "0 0 0 1px var(--chakra-colors-orange-500)",
            }}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
